import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LifeBuoy, PlayCircle, Search, Bug, Lightbulb, Clock, Send, Paperclip, X, Ticket, Sparkles, Bot, BookOpen, LifeBuoy as Buoy, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const IMG = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/help/image/${p}`;
const EMPTY = { type: "bug", subject: "", description: "", module: "", action: "", occurred_at: "", notes: "", screenshot: null };

function Steps({ steps }) {
  return (
    <ol className="list-decimal ml-5 space-y-2.5 text-sm text-slate-600 dark:text-slate-300">
      {(steps || []).map((s, i) => (
        <li key={i} className="space-y-1.5">
          <span>{s.text}</span>
          {s.image_path && (
            <div className="mt-1.5">
              <img src={IMG(s.image_path)} alt={s.caption || `step ${i + 1}`} className="rounded-lg border border-border max-h-64 object-contain" data-testid={`help-step-img-${i}`} />
              {s.caption && <p className="text-xs text-muted-foreground mt-1 italic">{s.caption}</p>}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

function RoleNote({ note }) {
  if (!note) return null;
  return (
    <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-2.5 py-1.5 mt-2" data-testid="help-role-note">
      <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {note}
    </p>
  );
}

// Uncategorised topics first (existing flat list), then each category as its own group in first-seen order.
const groupByCategory = (list) => { const m = new Map(); for (const t of list) { const k = t.category || ""; if (!m.has(k)) m.set(k, []); m.get(k).push(t); } return [...m.entries()].sort((a, b) => (a[0] === "" ? -1 : b[0] === "" ? 1 : 0)); };

function Tutorials({ onEscalate, focusSlug }) {
  const { openTour } = useAuth();
  const [topics, setTopics] = useState([]);
  const [openItem, setOpenItem] = useState("");
  useEffect(() => {
    if (!focusSlug || !topics.length) return;
    const t = topics.find((x) => x.slug === focusSlug);
    if (t) { setOpenItem(t.id); setTimeout(() => document.querySelector(`[data-testid="help-item-${focusSlug}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150); }
  }, [focusSlug, topics]);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  // Every typed word must appear in the title, keywords or synonyms (so "forgot pin" finds the topic).
  const matches = (a) => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return true;
    const hay = [a.title, ...(a.keywords || []), ...(a.synonyms || [])].join(" ").toLowerCase();
    return words.every((w) => hay.includes(w));
  };
  const [answer, setAnswer] = useState(null);
  const [pick, setPick] = useState(null);
  const [asking, setAsking] = useState(false);

  useEffect(() => { api.get("/help/topics").then((r) => setTopics(r.data)).catch(() => {}); }, []);

  const ask = async (e) => {
    e?.preventDefault();
    const question = q.trim();
    if (!question) return;
    setAsking(true); setAnswer(null); setPick(null);
    try {
      const { data } = await api.post("/help/ask", { question });
      setAnswer(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Couldn't search help"); }
    finally { setAsking(false); }
  };

  return (
    <div className="space-y-5">
      {/* Smart Ask box */}
      <Card className="p-4 sm:p-5 border-emerald-500/30" data-testid="help-ask-card">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-emerald-500" />
          <h2 className="font-head font-bold">Ask a question</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Type what you're trying to do and we'll find the right guide first. Only if there's no matching guide will the assistant help.</p>
        <form onSubmit={ask} className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. How do I delete a product?" data-testid="help-ask-input" />
          <Button type="submit" disabled={asking} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid="help-ask-submit">
            <Send className="w-4 h-4 mr-1.5" /> {asking ? "Searching…" : "Ask"}
          </Button>
        </form>

        {answer && (
          <div className="mt-4 rounded-xl border border-border bg-secondary/40 p-4" data-testid="help-answer">
            <div className="flex items-center gap-1.5 text-xs font-semibold mb-2">
              {answer.source === "tutorial"
                ? <><BookOpen className="w-3.5 h-3.5 text-emerald-500" /> <span data-testid="help-answer-source">Built-in guide</span></>
                : <><Bot className="w-3.5 h-3.5 text-sky-500" /> <span data-testid="help-answer-source">Assistant</span></>}
            </div>
            {answer.source === "tutorial" ? (
              (answer.matches && answer.matches.length > 1 && !pick) ? (
                <div data-testid="help-answer-options">
                  <p className="text-sm text-muted-foreground mb-2">A few guides match. Pick the closest:</p>
                  <div className="space-y-2">
                    {answer.matches.map((m, i) => (
                      <button key={i} onClick={() => setPick(m.topic)} data-testid={`help-match-${i}`}
                        className="w-full text-left rounded-lg border border-border hover:border-emerald-500/50 hover:bg-secondary p-3 transition-colors">
                        <p className="font-medium text-sm flex items-center gap-2">{m.topic.title}{i === 0 && <span className="text-[10px] text-emerald-600 font-semibold">Best match</span>}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{m.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  {answer.matches && answer.matches.length > 1 && (
                    <button onClick={() => setPick(null)} className="text-xs text-emerald-600 mb-2" data-testid="help-back-options">← Other matching guides</button>
                  )}
                  <p className="font-head font-bold mb-2">{(pick || answer.topic)?.title}</p>
                  <RoleNote note={(pick || answer.topic)?.role_note} />
                  <div className="mt-2"><Steps steps={(pick || answer.topic)?.steps} /></div>
                </div>
              )
            ) : (
              <p className="text-sm whitespace-pre-wrap text-slate-600 dark:text-slate-300" data-testid="help-answer-ai">{answer.answer}</p>
            )}
            <div className="mt-4 flex items-center gap-2 border-t border-border pt-3">
              <span className="text-xs text-muted-foreground">Still need help?</span>
              <Button size="sm" variant="outline" onClick={() => onEscalate(answer.escalate)} data-testid="help-escalate-btn">
                <Buoy className="w-3.5 h-3.5 mr-1.5" /> Contact Support
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Existing tutorial library (now DB-backed) */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search help topics…" className="pl-9" data-testid="help-search" />
        </div>
        <Button onClick={openTour} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="help-replay-tour">
          <PlayCircle className="w-4 h-4 mr-1.5" /> Replay Getting Started Tour
        </Button>
      </div>
      <Card className="p-2 sm:p-4">
        <Accordion type="single" collapsible value={openItem} onValueChange={setOpenItem} className="w-full" data-testid="help-accordion">
          {groupByCategory(topics.filter(matches)).map(([cat, items]) => (
            <div key={cat || "general"} data-testid={`help-category-${(cat || "general").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {cat && <p className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-emerald-600" data-testid="help-category-title">{cat}</p>}
              {items.map((a) => (
            <AccordionItem key={a.id} value={a.id} data-testid={`help-item-${a.slug}`}>
              <AccordionTrigger className="text-left font-medium">
                {a.title}{a.role_locked && <Lock className="w-3.5 h-3.5 ml-2 text-amber-500 inline" />}
              </AccordionTrigger>
              <AccordionContent>
                <RoleNote note={a.role_note} />
                <div className="mt-2"><Steps steps={a.steps} /></div>
              </AccordionContent>
            </AccordionItem>
              ))}
            </div>
          ))}
        </Accordion>
        {topics.filter(matches).length === 0 && (
          <p className="text-center text-muted-foreground py-8">No topics match "{search}".</p>
        )}
      </Card>
    </div>
  );
}

function StatusPill({ status }) {
  const map = { open: "bg-amber-500/15 text-amber-500 border-amber-500/30", in_progress: "bg-sky-500/15 text-sky-500 border-sky-500/30", resolved: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30", closed: "bg-slate-500/15 text-slate-500 border-slate-500/30" };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-md border capitalize ${map[status] || map.open}`}>{(status || "open").replace("_", " ")}</span>;
}

function ContactSupport({ prefill }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (prefill && (prefill.subject || prefill.description)) {
      setForm((f) => ({ ...f, subject: prefill.subject || f.subject, description: prefill.description || f.description }));
    }
  }, [prefill]);

  const loadTickets = async () => {
    try { const { data } = await api.get("/support/me"); setTickets(data || []); } catch { /* ignore */ }
  };
  useEffect(() => { loadTickets(); }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Screenshot must be under 4 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => set("screenshot", reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) { toast.error("Please add a subject and a description."); return; }
    setSubmitting(true);
    try {
      await api.post("/support", form);
      toast.success(form.type === "bug" ? "Bug report submitted. Our team has been notified." : "Feature request submitted. Thank you!");
      setForm(EMPTY);
      loadTickets();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const isBug = form.type === "bug";

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="lg:col-span-3 p-5 sm:p-6" data-testid="support-form-card">
        <div className="flex items-center gap-2 mb-1">
          <h2 className="font-head text-lg font-bold">Contact Support &amp; Report a Bug</h2>
        </div>
        <p className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-5">
          <Clock className="w-3.5 h-3.5" /> Submit anytime. This portal is open 24/7 and reports go straight to our team.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="mb-2 block text-sm">What would you like to do?</Label>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => set("type", "bug")} data-testid="support-type-bug"
                className={`flex items-center gap-2 rounded-xl border p-3 text-left text-sm transition-colors ${isBug ? "border-red-500/50 bg-red-500/10 text-red-500" : "border-border hover:bg-secondary text-muted-foreground"}`}>
                <Bug className="w-4 h-4 shrink-0" /> <span><b className="block text-foreground">Report a Software Defect</b> Something isn't working right</span>
              </button>
              <button type="button" onClick={() => set("type", "feature")} data-testid="support-type-feature"
                className={`flex items-center gap-2 rounded-xl border p-3 text-left text-sm transition-colors ${!isBug ? "border-violet-500/50 bg-violet-500/10 text-violet-500" : "border-border hover:bg-secondary text-muted-foreground"}`}>
                <Lightbulb className="w-4 h-4 shrink-0" /> <span><b className="block text-foreground">Request a Feature</b> Suggest an improvement</span>
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="sup-subject" className="mb-1.5 block text-sm">Subject</Label>
            <Input id="sup-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)}
              placeholder={isBug ? "e.g. Receipt won't print after a credit sale" : "e.g. Add barcode scanning to POS"} data-testid="support-subject" />
          </div>

          <div>
            <Label htmlFor="sup-desc" className="mb-1.5 block text-sm">{isBug ? "Describe the problem" : "Describe the feature"}</Label>
            <Textarea id="sup-desc" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)}
              placeholder={isBug ? "What happened? What did you expect to happen?" : "What should it do and why would it help you?"} data-testid="support-description" />
          </div>

          {isBug && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sup-module" className="mb-1.5 block text-sm">Where did it happen? <span className="text-muted-foreground">(optional)</span></Label>
                <Input id="sup-module" value={form.module} onChange={(e) => set("module", e.target.value)} placeholder="e.g. POS Register, Inventory" data-testid="support-module" />
              </div>
              <div>
                <Label htmlFor="sup-action" className="mb-1.5 block text-sm">What were you doing? <span className="text-muted-foreground">(optional)</span></Label>
                <Input id="sup-action" value={form.action} onChange={(e) => set("action", e.target.value)} placeholder="e.g. Completing a cash sale" data-testid="support-action" />
              </div>
            </div>
          )}

          <div>
            <Label className="mb-1.5 block text-sm">Screenshot <span className="text-muted-foreground">(optional)</span></Label>
            {form.screenshot ? (
              <div className="flex items-center gap-3">
                <img src={form.screenshot} alt="attachment" className="h-16 w-16 rounded-lg object-cover border border-border" data-testid="support-screenshot-preview" />
                <Button type="button" variant="outline" size="sm" onClick={() => set("screenshot", null)} data-testid="support-screenshot-remove">
                  <X className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <label className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground rounded-xl border border-dashed border-border px-3 py-2.5 hover:bg-secondary w-fit">
                <Paperclip className="w-4 h-4" /> Attach an image
                <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="support-screenshot-input" />
              </label>
            )}
          </div>

          <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="support-submit">
            <Send className="w-4 h-4 mr-1.5" /> {submitting ? "Submitting…" : "Submit"}
          </Button>
        </form>
      </Card>

      <Card className="lg:col-span-2 p-5 sm:p-6" data-testid="support-tickets-card">
        <div className="flex items-center gap-2 mb-4">
          <Ticket className="w-4 h-4" />
          <h2 className="font-head text-lg font-bold">My Submitted Tickets</h2>
        </div>
        {tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">You haven't submitted any tickets yet.</p>
        ) : (
          <div className="space-y-3 max-h-[520px] overflow-y-auto">
            {tickets.map((t) => (
              <div key={t.id} className="rounded-xl border border-border p-3" data-testid={`support-ticket-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold">
                    {t.type === "bug" ? <Bug className="w-3.5 h-3.5 text-red-500" /> : <Lightbulb className="w-3.5 h-3.5 text-violet-500" />}
                    {t.type === "bug" ? "Bug" : "Feature"}
                  </div>
                  <StatusPill status={t.status} />
                </div>
                <p className="text-sm font-medium mt-1.5">{t.subject}</p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.description}</p>
                {(t.replies || []).length > 0 && (
                  <div className="mt-2 space-y-1.5 border-t border-border pt-2" data-testid={`support-replies-${t.id}`}>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600">NEEC Support replied</p>
                    {t.replies.map((r) => (
                      <div key={r.id} className="text-xs bg-secondary rounded-lg p-2">
                        <span className="font-semibold">{r.by}</span> <span className="text-muted-foreground">· {(r.at || "").slice(0, 10)}</span>
                        <p className="mt-0.5 whitespace-pre-wrap">{r.message}</p>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground/70 mt-1.5">{(t.created_at || "").slice(0, 10)}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default function Help() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "support" ? "support" : "tutorials");
  const [prefill, setPrefill] = useState(params.get("subject") || params.get("desc") ? { subject: params.get("subject") || "", description: params.get("desc") || "" } : null);
  const focusSlug = params.get("topic") || "";

  const escalate = (e) => { setPrefill(e || null); setTab("support"); };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><LifeBuoy className="w-7 h-7" /> Help &amp; Support</h1>
        <p className="text-muted-foreground">Ask a question, browse step-by-step guides, or reach our team 24/7.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="help-tabs">
          <TabsTrigger value="tutorials" data-testid="help-tab-tutorials">Tutorials</TabsTrigger>
          <TabsTrigger value="support" data-testid="help-tab-support">Contact Support</TabsTrigger>
        </TabsList>
        <TabsContent value="tutorials" className="mt-5"><Tutorials onEscalate={escalate} focusSlug={focusSlug} /></TabsContent>
        <TabsContent value="support" className="mt-5"><ContactSupport prefill={prefill} /></TabsContent>
      </Tabs>
    </div>
  );
}
